// Two merchandising boosts applied to whatever collection/search grid the customer lands on --
// REORDER ONLY, never hides a product. Both are stable partitions (original relative order, i.e.
// Shopify's own best-selling/relevance order, is preserved WITHIN each tier) so this only ever
// nudges an already-sound order, never replaces it with something arbitrary.
//
// 1) VEHICLE FIT -- products that fit the customer's remembered vehicle (assets/
//    vehicle-memory-bar.js, localStorage key esSelectedVehicle) move toward the front.
//    "Show what fits my vehicle first, then everything else; if nothing fits, leave the default
//    order alone" per Jeff's spec.
// 2) EUROSPEED VENDOR (added 2026-09-17, Jeff): EuroSpeed's own ~250 products are a small slice
//    of a 3,900+ product catalog (mostly GERSpeed-dropshipped brands), and a customer told Jeff
//    it now takes forever to scroll to EuroSpeed's own stuff. "Always prioritize EuroSpeed
//    products... unless what they're looking for obviously isn't [EuroSpeed]." So EuroSpeed-
//    vendor items (data-vendor="eurospeed", set in sections/collection-template.liquid and
//    templates/search.liquid) also move toward the front, on both collection AND search pages,
//    with or without a vehicle selected.
//
// GUARD (real incident, 2026-09-17): a customer with a Volkswagen Golf GTI remembered searched
// "BMW M3 diffuser" and got Golf GTI diffusers (which only loosely matched the word "diffuser")
// shoved in FRONT of the actual, exact "BMW M3" diffuser, because the old version of this script
// blindly moved every vehicle-fit item to the top regardless of how well it matched the typed
// query. Jeff's own words: "if it's not the item that they search for, don't prioritize showing
// something that's compatible with their car over showing the actual item that they search for"
// -- and the same principle applies to the vendor boost: "if they do a search for something
// that's obviously from a different vendor, then show that [instead]." Fix: on a SEARCH page, if
// the typed query itself already names a different make/model than the remembered vehicle, OR
// names a specific non-EuroSpeed vendor/brand that's actually present in these results, skip
// BOTH boosts entirely for that page and trust Shopify's own relevance ordering untouched -- the
// customer is explicitly shopping for something specific in that moment, and neither "fits my
// vehicle" nor "is a EuroSpeed product" should ever outrank "matches what I actually typed."
(function(){
  const ctx = document.getElementById('es-ctx');
  const grid = document.getElementById('es-grid');
  if (!ctx || !grid) return;

  const mode = ctx.getAttribute('data-mode') || 'collection';
  const collectionUrl = (ctx.getAttribute('data-collection-url') || '').replace(/\/$/, '');
  const currentTags = (ctx.getAttribute('data-current-tags') || '').split('|').map(t => t.trim().toLowerCase()).filter(Boolean);
  const searchTerms = ctx.getAttribute('data-search-terms') || '';

  let vehicle;
  try { vehicle = JSON.parse(localStorage.getItem('esSelectedVehicle') || 'null'); }
  catch (_) { vehicle = null; }
  const vTags = (vehicle && Array.isArray(vehicle.tags)) ? vehicle.tags.map(t => String(t).toLowerCase()).filter(Boolean) : [];
  // Already viewing a page scoped to (at least) this vehicle -- e.g. the customer is on their
  // vehicle's own pill-filtered page already -- nothing to prioritize on the vehicle axis.
  const vehicleAlreadyScoped = vTags.length > 0 && vTags.every(t => currentTags.indexOf(t) !== -1);
  const wantVehicleBoost = vTags.length > 0 && !vehicleAlreadyScoped;

  function escapeRe(s){ return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); }

  // Does the free-text query itself name a make/model that ISN'T part of the remembered
  // vehicle? e.g. "BMW M3 diffuser" while a VW Golf GTI is remembered. Checks whole-word matches
  // against every known make/model name+alias (assets/vehicle-tag-maps.js) so "gti" in a search
  // for the customer's OWN car doesn't false-trigger, but "bmw"/"m3" does. Skips single-
  // character aliases (e.g. the bare "r" alias for Golf R) to avoid matching a stray letter.
  function mentionsDifferentVehicle(text){
    if (mode !== 'search' || !text) return false;
    const VT = window.ESVehicleTags;
    if (!VT) return false;
    const lower = text.toLowerCase();
    const groups = [VT.MAKE_TAGS, VT.MODEL_TAGS];
    for (const group of groups){
      for (const key of Object.keys(group)){
        const aliases = [key.toLowerCase()].concat((group[key] || []).map(a => String(a).toLowerCase()));
        const uniq = Array.from(new Set(aliases)).filter(a => a && a.length > 1);
        const matched = uniq.some(a => new RegExp('\\b' + escapeRe(a) + '\\b', 'i').test(lower));
        if (!matched) continue;
        const belongsToCurrentVehicle = uniq.some(a => vTags.indexOf(a) !== -1);
        if (!belongsToCurrentVehicle) return true; // names a different make/model -- bail
      }
    }
    return false;
  }

  // Does the free-text query name a specific vendor/brand (other than EuroSpeed) that's actually
  // among today's results? Built from the vendors ACTUALLY PRESENT in this grid (via each card's
  // data-vendor attribute) rather than a hardcoded brand list, so it stays correct as vendors are
  // added/removed with zero maintenance here.
  function mentionsDifferentVendor(text, items){
    if (mode !== 'search' || !text) return false;
    const lower = text.toLowerCase();
    const vendors = new Set();
    items.forEach(item => {
      const v = (item.getAttribute('data-vendor') || '').trim().toLowerCase();
      if (v && v !== 'eurospeed') vendors.add(v);
    });
    for (const v of vendors){
      if (v.length > 1 && new RegExp('\\b' + escapeRe(v) + '\\b', 'i').test(lower)) return true;
    }
    return false;
  }

  function tagTermFor(t){ return /[\s.]/.test(t) ? `tag:"${t}"` : `tag:${t}`; }

  function combinedPath(){
    const allTags = currentTags.concat(vTags);
    if (mode === 'search' || allTags.some(t => t.indexOf('.') !== -1)) {
      const q = [searchTerms].concat(allTags.map(tagTermFor)).filter(Boolean).join(' ');
      return `/search?q=${encodeURIComponent(q)}&type=product`;
    }
    return (collectionUrl || '/collections/all') + '/' + allTags.map(t => encodeURIComponent(t)).join('+');
  }

  function productKeyFor(item){
    const link = item.querySelector('a[href*="/products/"]');
    if (!link) return null;
    const m = link.getAttribute('href').match(/\/products\/([^/?#]+)/);
    return m ? m[1] : null;
  }

  function isEuroSpeed(item){
    return (item.getAttribute('data-vendor') || '').trim().toLowerCase() === 'eurospeed';
  }

  // Direct children only -- the theme's own card markup (c-product-item-grid-v1) nests an inner
  // element that ALSO carries the "product-item" class inside the outer col-md-4 wrapper, so a
  // descendant query (querySelectorAll) matches both; appendChild-moving that inner node rips it
  // out of its wrapper (the "stacked one-by-one" bug).
  function directItems(container){
    return Array.from(container.children).filter(el => el.classList && el.classList.contains('product-item'));
  }

  // Stable partition into 4 priority tiers, preserving each item's original relative order
  // within its own tier (that original order IS Shopify's best-selling/relevance ranking --
  // this only ever regroups it, never re-ranks within a group):
  //   1. fits vehicle AND EuroSpeed      2. fits vehicle, other vendor
  //   3. EuroSpeed, doesn't fit vehicle  4. everything else
  function reorder(currentItems, matchedKeys){
    const tiers = [[], [], [], []];
    currentItems.forEach(item => {
      const k = productKeyFor(item);
      const fits = !!(matchedKeys && k && matchedKeys.has(k));
      const es = isEuroSpeed(item);
      const idx = fits ? (es ? 0 : 1) : (es ? 2 : 3);
      tiers[idx].push(item);
    });
    return tiers;
  }

  function applyTiers(tiers, showDivider){
    const fits = tiers[0].concat(tiers[1]);
    const rest = tiers[2].concat(tiers[3]);
    if (!fits.length || !rest.length) return; // nothing to split -- leave default order
    const frag = document.createDocumentFragment();
    fits.forEach(item => frag.appendChild(item));
    if (showDivider){
      const divider = document.createElement('div');
      divider.style.cssText = 'flex:0 0 100%;width:100%;margin:18px 0 10px;padding-top:14px;border-top:1px solid #e5e5e5;font-size:12px;letter-spacing:.12em;text-transform:uppercase;color:#888;';
      divider.textContent = 'Fits ' + (vehicle && vehicle.label ? vehicle.label : 'your vehicle') + ' — more products below';
      frag.appendChild(divider);
    }
    rest.forEach(item => frag.appendChild(item));
    // insertBefore, not appendChild (2026-10-29): search-results-padding.js may already have
    // appended filler best-sellers while the vehicle fetch above was in flight; appending here
    // used to push those unrelated fillers ABOVE every real result.
    grid.insertBefore(frag, grid.firstChild);
  }

  // EuroSpeed-vendor boost alone (no vehicle-fit concept in play -- either no vehicle is
  // remembered, or nothing on this page fits the one that is). No divider: there's nothing to
  // explain to the customer, it's just a quiet reorder.
  function applyVendorOnly(currentItems){
    const esItems = currentItems.filter(isEuroSpeed);
    const restItems = currentItems.filter(item => !isEuroSpeed(item));
    if (!esItems.length || !restItems.length) return;
    const frag = document.createDocumentFragment();
    esItems.forEach(item => frag.appendChild(item));
    restItems.forEach(item => frag.appendChild(item));
    grid.insertBefore(frag, grid.firstChild);
  }

  // SEARCH pages (2026-10-29, Jeff: "the most relevant product to the query ranks highest"):
  // templates/search.liquid now ranks server-side (title/vendor match > tag match > body-only,
  // EuroSpeed first only inside a matching tier) and marks each card data-match="1|0". The old
  // client-side EuroSpeed boost moved EVERY EuroSpeed card in the grid to the top, even ones that
  // only matched on body text (#62 and #74 jumped above the Zeroline gauges for "zero line"), so
  // it no longer runs on search. The remembered-vehicle boost is kept but only regroups the
  // MATCHING cards (fits first, original order otherwise); non-matching cards never move up.
  function applySearchVehicle(currentItems, matchedKeys){
    const isMatch = item => item.getAttribute('data-match') === '1';
    const matched = currentItems.filter(isMatch);
    if (!matched.length) return;
    const fit = [], nofit = [];
    matched.forEach(item => { const k = productKeyFor(item); (k && matchedKeys.has(k) ? fit : nofit).push(item); });
    if (!fit.length || !nofit.length) return;
    const frag = document.createDocumentFragment();
    fit.concat(nofit).forEach(item => frag.appendChild(item));
    grid.insertBefore(frag, grid.firstChild);
  }

  async function run(){
    const currentItems = directItems(grid);
    if (!currentItems.length) return;

    if (mentionsDifferentVehicle(searchTerms) || mentionsDifferentVendor(searchTerms, currentItems)) return;

    if (mode === 'search' && !wantVehicleBoost) return; // server-side ranking already applied

    if (!wantVehicleBoost){
      applyVendorOnly(currentItems);
      return;
    }

    let res;
    try { res = await fetch(combinedPath(), { cache: 'no-store' }); }
    catch (_) { return; }
    if (!res.ok) return;
    const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
    const fetchedGrid = doc.getElementById('es-grid');
    const matchedItems = fetchedGrid ? directItems(fetchedGrid) : [];
    if (!matchedItems.length){
      // Nothing on this page fits the remembered vehicle -- still apply the EuroSpeed boost
      // alone rather than giving up on both (collection pages only; search is ranked server-side).
      if (mode !== 'search') applyVendorOnly(currentItems);
      return;
    }

    const matchedKeys = new Set();
    matchedItems.forEach(item => { const k = productKeyFor(item); if (k) matchedKeys.add(k); });
    if (mode === 'search') { applySearchVehicle(currentItems, matchedKeys); return; }

    const tiers = reorder(currentItems, matchedKeys);
    applyTiers(tiers, true);
  }

  run();
})();
